import { FC, useCallback, useEffect, useRef, useState } from 'react'

import {
  Button,
  Flex,
  HStack,
  Icon,
  Input,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Square,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { ConfirmIcon, EditBoxIcon } from '@editor/assets/icons'
import { InspectionGroup } from '@editor/interfaces/inspectionGroup'
import { isInspectionGroup, validateName } from '@editor/services/InspectionGroup'
import { RootState } from '@editor/store/app'
import { Tooltip, useOutsideClick } from '@infraloop/common'
import { getContrastingColor } from '@uiw/react-color'
import Chrome, { ChromeInputType } from '@uiw/react-color-chrome'
import { useSelector } from 'react-redux'

const PopoverEditingGroup: FC<{
  group: InspectionGroup
  onComplete: (name: string, color: string) => void
}> = ({ group, onComplete }) => {
  // Hooks
  const { onOpen, onClose, open } = useDisclosure()

  // Store
  const groups = useSelector((state: RootState) => state.inspectionGroup.groups).filter(
    (g) =>
      isInspectionGroup(g.item) &&
      g.item.group_id !== group.group_id &&
      group.inspection_area_id === g.item.inspection_area_id,
  ) // for unique name validation

  // State
  const [editedName, setEditedName] = useState(group.name)
  const [editedColor, setEditedColor] = useState(group.color)
  const [nameError, setNameError] = useState<string | null>(null)

  // Ref
  const containerRef = useRef<HTMLDivElement>(null)
  const nameEditRef = useRef<HTMLInputElement>(null)

  /**
   * Update the edited name when the group changes.
   * This is useful when the group is updated from outside this component.
   */
  useEffect(() => {
    setEditedName(group.name)
  }, [group])

  /**
   * Try to focus the input element.
   */
  const tryFocus = useCallback(() => {
    if (nameEditRef.current) {
      nameEditRef.current.focus()
      return
    }

    setTimeout(() => tryFocus(), 100)
  }, [nameEditRef])

  /**
   * Handle the close event of the popover.
   */
  const handleClose = () => {
    const err = validateName(editedName, group.group_id, groups)
    if (typeof err === 'string') {
      setNameError(err)
      return
    }

    if (onComplete) {
      onComplete(editedName, editedColor)
    }
    setNameError(null)
    onClose()
  }

  const handleUpdatePage = useCallback(
    (name: string, color: string) => {
      const rows: NodeListOf<HTMLElement> = document.querySelectorAll(`.data-rows[data-group-id="${group.group_id}"]`)

      if (!rows.length) return

      rows.forEach((row) => {
        const title: HTMLElement | null = row.querySelector('.title')
        const groupName = title?.querySelector('.group-name')

        row.style.setProperty('--group-color', color)
        if (title) title.style.color = getContrastingColor(color)
        if (groupName) groupName.textContent = name
      })
    },
    [group.group_id],
  )

  /**
   * Handle the reset event to restore the original group name and color.
   */
  const handleReset = useCallback(() => {
    setNameError(null)
    setEditedName(group.name)
    setEditedColor(group.color)
    handleUpdatePage(group.name, group.color)
  }, [group.name, group.color, handleUpdatePage])

  /**
   * Handle outside click.
   */
  useOutsideClick({
    ref: nameEditRef,
    handler: () => {
      if (!editedName) {
        handleReset()
      }
      onClose()
    },
  })

  return (
    <Flex ref={containerRef} gap={2} className="popover-editing">
      <Popover.Root
        positioning={{ placement: 'top-start' }}
        open={open}
        onOpenChange={(isOpen) => {
          if (isOpen) {
            tryFocus()
            onOpen()
          }
        }}
      >
        <PopoverTrigger>
          <Square>
            <Icon as={EditBoxIcon} boxSize={5} />
          </Square>
        </PopoverTrigger>
        <Portal>
          <Popover.Positioner>
            <PopoverContent>
              <PopoverArrow />
              <PopoverBody>
                <HStack
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      e.stopPropagation()
                      handleClose()
                    } else if (e.key === 'Escape') {
                      e.preventDefault()
                      e.stopPropagation()
                      handleReset()
                      onClose()
                    }
                  }}
                >
                  {/* Color */}
                  <Popover.Root>
                    <PopoverTrigger>
                      <Button
                        variant="outline"
                        width={10}
                        height={10}
                        p={0}
                        ml={1}
                        backgroundColor={editedColor}
                        borderColor="gray2.300"
                        borderRadius={4}
                        _hover={{ bg: editedColor }}
                        _focus={{ bg: editedColor }}
                        _active={{ bg: editedColor }}
                        _disabled={{ bg: editedColor }}
                        onClick={(e) => {
                          e.stopPropagation()
                        }}
                      />
                    </PopoverTrigger>
                    <PopoverContent w="auto" onClick={(e) => e.stopPropagation()}>
                      <Chrome
                        inputType={ChromeInputType.HEXA}
                        showAlpha={false}
                        showEyeDropper={false}
                        color={editedColor}
                        onChange={(clr) => {
                          setEditedColor(clr.hex)
                          handleUpdatePage(editedName, clr.hex)
                        }}
                      />
                    </PopoverContent>
                  </Popover.Root>

                  {/* Name */}
                  <Tooltip
                    label={nameError ? <Text color="orange.300">{nameError}</Text> : undefined}
                    open={!!nameError}
                    positioning={{ placement: 'top' }}
                    showArrow
                  >
                    <Input
                      ref={nameEditRef}
                      defaultValue={group.name}
                      onChange={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                        setEditedName(event.target.value)
                        handleUpdatePage(event.target.value, editedColor)

                        if (event.target.value.trim()) {
                          setNameError(null)
                        }
                      }}
                      required
                    />
                  </Tooltip>
                  <Button colorPalette="blue" onClick={handleClose}>
                    <Icon as={ConfirmIcon} boxSize={5} />
                  </Button>
                </HStack>
              </PopoverBody>
            </PopoverContent>
          </Popover.Positioner>
        </Portal>
      </Popover.Root>
      <Text fontWeight="bold" className="group-name">
        {editedName}
      </Text>
    </Flex>
  )
}

export default PopoverEditingGroup
